import { Map as ImmutableMap, List } from 'immutable';
import compose from 'transmute/compose';
import { createSelector } from 'reselect';
import { getIsUngatedToForecastingMultipleOutcomes } from '../selectors/authSelectors';
import { getUsersMap, getTeamsMap, getUsersToTeamNameMap } from './usersSelectors';
import { getOwnersMap, getOwnerIdToUserIdMap, getCurrentOwnerId } from './ownersSelectors';
import { getDealSidebarId, getDealSidebarIsForTeam, getIsOverviewDataFetching, getSelectedTeamId } from './overviewSelectors';
import { getSelectedDetailOwnerId } from './detailViewSelectors';
import { getCompanyCurrency } from './multiCurrencySelectors';
import { getHasForecastTypesFetchError } from './forecastSelectors';
import { buildDealRollup, buildOpenPipelineCoverage, buildTeamGoal, buildTeamInfo, buildTeamSubmission, buildTotalGoal, buildTotalInfo, buildTotalSubmission, buildUserGoal, buildUserInfo, buildUserSubmission } from '../utils/aggregatedOverviewDataUtils';
import { createOverviewTableRow } from '../utils/overviewTableRowUtils';
import { buildTypedMap } from '../types/TypedMap';
const getAggregatedOverviewData = state => state.aggregatedOverviewData;
const getDealRollups = createSelector([getAggregatedOverviewData], aggregatedOverviewData => aggregatedOverviewData.get('dealRollups'));
export const getDealRollupsCurrencyCode = createSelector([getDealRollups, getCompanyCurrency], (dealRollups, companyCurrency) => dealRollups.get('currencyCode') || companyCurrency);
export const getInitialRow = createSelector([getDealRollupsCurrencyCode], currencyCode => createOverviewTableRow({
  closed: {
    value: 0,
    currencyCode
  },
  attainment: 0,
  totalForecastedAmount: {
    value: 0,
    currencyCode
  }
}));
const getEmptyDealRollup = createSelector([getDealRollupsCurrencyCode], currencyCode => buildTypedMap({
  count: 0,
  totalForecastedAmount: {
    value: 0,
    currencyCode
  },
  totalAmount: {
    value: 0,
    currencyCode
  },
  totalAmountInHomeCurrency: {
    value: 0,
    currencyCode
  },
  aggregationsByForecastCategory: List()
}));

// When we gracefully handle bad pipeline, this is momentarily not set
// which throws an error, lets default to empty array here
export const getOmittedDealGroupingIds = state => getDealRollups(state).get('excludedCategories') || List();
export const getClosedDealGroupingIds = state => getDealRollups(state) ? getDealRollups(state).get('attainmentCategories') : List();
export const getGoals = createSelector([getAggregatedOverviewData], aggregatedOverviewData => aggregatedOverviewData.get('goals'));
export const getSubmissions = createSelector([getAggregatedOverviewData], aggregatedOverviewData => aggregatedOverviewData.get('submissions'));
export const getSubmissionForSelectedOwner = createSelector([getSubmissions, getCurrentOwnerId], (submissions, ownerId) => {
  if (!ownerId) {
    return null;
  }
  const ownerSubmissions = submissions.getIn(['displayedAggregationsByOwner', `${ownerId}`]) || null;
  return ownerSubmissions;
});
export const getQuotasForOwnersCount = createSelector([getGoals], goals => {
  const displayedAggregationsByOwnerCount = goals.get('displayedAggregationsByOwner') ? goals.get('displayedAggregationsByOwner').size : 0;
  const displayedAggregationsByTeamCount = goals.get('displayedAggregationsByTeam') ? goals.get('displayedAggregationsByTeam').size : 0;
  return displayedAggregationsByOwnerCount || displayedAggregationsByTeamCount;
});
export const getOwnerIds = createSelector([getDealRollups], dealRollups => dealRollups.get('displayedAggregationsByOwner', ImmutableMap()).keySeq().toSet().union(dealRollups.get('ownerIdsWithoutDeals', List()).toSet()).map(ownerId => +ownerId));
const getTeamIds = createSelector([getDealRollups], dealRollups => dealRollups.get('displayedAggregationsByTeam', ImmutableMap()).keySeq().toSet().union(dealRollups.get('teamIdsWithoutDeals', List()).toSet()).map(teamId => +teamId));

// NOTE: If we end up rewriting the overview table and related redux,
// we should consider making these selectors more pure and putting
// all this data massaging logic into utilities
const getUserRows = createSelector([getClosedDealGroupingIds, getDealRollups, getEmptyDealRollup, getGoals, getInitialRow, getOwnerIds, getOwnerIdToUserIdMap, getOwnersMap, getUsersToTeamNameMap, getSelectedDetailOwnerId, getSubmissions], (closedDealGroupingIds, dealRollups, emptyDealRollup, goals, initialRow, ownerIds, ownerIdToUserIdMap, ownersMap, usersToTeamNameMap, selectedDetailOwnerId, submissions) => {
  var _ownerIds$filter$redu;
  return (_ownerIds$filter$redu = ownerIds
  // if in the detail view, we want to filter the current owner
  .filter(ownerId => !selectedDetailOwnerId || ownerId === selectedDetailOwnerId).reduce((rows, ownerId) => {
    const dealRollup = dealRollups.getIn(['displayedAggregationsByOwner', `${ownerId}`], emptyDealRollup);
    const goal = goals.getIn(['displayedAggregationsByOwner', `${ownerId}`, 'amount'], {
      value: 0,
      currencyCode: ''
    });
    const submission = submissions.getIn(['displayedAggregationsByOwner', `${ownerId}`], ImmutableMap());
    const row = compose(buildUserSubmission({
      submission
    }), buildDealRollup({
      dealRollup,
      closedDealGroupingIds
    }), buildUserGoal({
      goal
    }), buildUserInfo({
      ownerId,
      ownersMap,
      ownerIdToUserIdMap,
      usersToTeamNameMap
    }))(initialRow);
    return rows.push(row);
  }, List())
  // This sort is necessary for our acceptance tests - it makes the
  // display order reliable when several users have attainments of 0
  ) === null || _ownerIds$filter$redu === void 0 ? void 0 : _ownerIds$filter$redu.sort((a, b) => b.name.localeCompare(a.name));
});
export const getTeamRows = createSelector([getClosedDealGroupingIds, getDealRollups, getEmptyDealRollup, getGoals, getInitialRow, getSelectedDetailOwnerId, getSubmissions, getTeamIds, getTeamsMap, getUsersMap
// Warning: can't add more selectors here - capacity is 12
], (closedDealGroupingIds, dealRollups, emptyDealRollup, goals, initialRow, selectedDetailOwnerId, submissions, teamIds, teamsMap) => {
  var _teamIds$filterNot$re;
  return (_teamIds$filterNot$re = teamIds.filterNot(() => !!selectedDetailOwnerId).reduce((rows, teamId) => {
    const dealRollup = dealRollups.getIn(['displayedAggregationsByTeam', `${teamId}`], emptyDealRollup);
    let activeUsersInTeam = dealRollup && dealRollup.get('activeUsersInTeam');
    let deactivatedUsersInTeam = dealRollup && dealRollup.get('deactivatedUsersInTeam');
    if (activeUsersInTeam === undefined && deactivatedUsersInTeam === undefined) {
      const detailedListOfTeamsWithoutDeals = dealRollups && dealRollups.get('detailedTeamIdsWithoutDeals');
      const team = detailedListOfTeamsWithoutDeals.find(teamObj => teamObj.teamId === teamId);
      activeUsersInTeam = team && team.activeUserCount;
      deactivatedUsersInTeam = team && team.deactivatedUserCount;
    }
    const teamInfo = buildTeamInfo({
      teamId,
      teamsMap,
      activeUsersInTeam,
      deactivatedUsersInTeam
    });
    const goal = goals.getIn(['displayedAggregationsByTeam', `${teamId}`, 'amount'], {
      value: 0,
      currencyCode: ''
    });
    const assigneeType = goals.getIn(['displayedAggregationsByTeam', `${teamId}`, 'assigneeType'], 0);
    const submission = submissions.getIn(['displayedAggregationsByTeam', `${teamId}`], ImmutableMap());
    const row = compose(buildTeamSubmission({
      submission
    }), buildDealRollup({
      dealRollup,
      closedDealGroupingIds
    }), buildTeamGoal({
      goal,
      assigneeType
    }), teamInfo)(initialRow);
    return rows.push(row);
  }, List())
  // This sort is necessary for our acceptance tests - it makes the
  // display order reliable when several users have attainments of 0
  ) === null || _teamIds$filterNot$re === void 0 ? void 0 : _teamIds$filterNot$re.sort((a, b) => b.name.localeCompare(a.name));
});
export const getRows = createSelector([getTeamRows, getUserRows, getIsOverviewDataFetching, getSelectedDetailOwnerId], (teamRows, userRows, isOverviewDataFetching, selectedDetailOwnerId) => {
  if (isOverviewDataFetching) {
    return List();
  }
  if (selectedDetailOwnerId) {
    return userRows;
  }
  return teamRows.concat(userRows);
});
export const getRepRowData = createSelector([getRows], rows => {
  return rows.first() || null;
});
export const getTotalRow = createSelector([getClosedDealGroupingIds, getDealRollups, getEmptyDealRollup, getGoals, getInitialRow, getIsOverviewDataFetching, getSelectedTeamId, getSubmissions, getTeamsMap], (closedDealGroupingIds, dealRollups, emptyDealRollup, goals, initialRow, isOverviewDataFetching, selectedTeamId, submissions, teamsMap) => {
  if (isOverviewDataFetching) {
    return createOverviewTableRow({});
  }
  const dealRollup = dealRollups.get('totalRollUp') || emptyDealRollup;
  const goal = goals.getIn(['totalAggregatedGoals', 'amount'], {
    value: 0,
    currencyCode: ''
  });
  const assigneeType = goals.getIn(['totalAggregatedGoals', 'assigneeType'], null);
  const submission = submissions.get('totalForecastSubmission', ImmutableMap());
  return compose(buildTotalSubmission({
    submission
  }), buildDealRollup({
    dealRollup,
    closedDealGroupingIds
  }), buildTotalGoal({
    goal,
    assigneeType
  }), buildTotalInfo({
    id: selectedTeamId,
    teamsMap
  }))(initialRow);
});
export const getOpenPipelineValue = createSelector([getAggregatedOverviewData, getIsOverviewDataFetching], (aggregatedOverviewData, isOverviewDataFetching) => {
  if (isOverviewDataFetching) {
    return null;
  }
  return aggregatedOverviewData.get('openPipelineValue');
});
export const getOpenPipelineValueFetched = createSelector([getAggregatedOverviewData, getIsOverviewDataFetching], (aggregatedOverviewData, isOverviewDataFetching) => {
  if (isOverviewDataFetching) {
    return null;
  }
  return aggregatedOverviewData.get('openPipelineValueFetched');
});
export const getOpenPipelineCoverage = createSelector([getOpenPipelineValue, getTotalRow], (openPipelineValue, totalRow) => {
  const goal = totalRow.goal;
  const closed = totalRow.closed;
  return buildOpenPipelineCoverage({
    closed: closed === null || closed === void 0 ? void 0 : closed.value,
    goal: goal === null || goal === void 0 ? void 0 : goal.value,
    openPipelineValue: openPipelineValue === null || openPipelineValue === void 0 ? void 0 : openPipelineValue.value
  });
});
export const getRowForId = createSelector([getDealSidebarId, getDealSidebarIsForTeam, getUserRows, getTeamRows, getTotalRow], (id, isTeam, userRows, teamRows, totalRow) => {
  const rows = isTeam ? teamRows.push(totalRow) : userRows;
  const rowData = rows.find(row => row && row.id === id);
  return rowData;
});
export const getHasFilteredDeactivatedUsers = createSelector([getDealRollups], dealRollups => dealRollups.get('hasFilteredDeactivatedUsers', false));
export const getGoalsBreakdown = createSelector([getAggregatedOverviewData], aggregatedOverviewData => aggregatedOverviewData.get('goalsBreakdown'));
export const getIsFetchingGoalsBreakdown = createSelector([getAggregatedOverviewData], aggregatedOverviewData => aggregatedOverviewData.get('isFetchingGoals'));
export const getHasAllPipelinesGoal = createSelector([getAggregatedOverviewData, getDealSidebarId, getDealSidebarIsForTeam, getTotalRow], (aggregatedOverviewData, ownerId, isTeam, totalRow) => {
  const isTeamTotalsRow = isTeam && totalRow.id === ownerId;
  if (isTeamTotalsRow) {
    return aggregatedOverviewData.getIn(['goals', 'totalAggregatedGoals', 'isAllPipelines'], false);
  }
  const goalKey = isTeam ? 'displayedAggregationsByTeam' : 'displayedAggregationsByOwner';
  return aggregatedOverviewData.getIn(['goals', goalKey, `${ownerId}`, 'isAllPipelines'], false);
});
export const getHasAggregatedOverviewDataDealRollupsFetchError = createSelector([getAggregatedOverviewData], aggregatedOverviewData => aggregatedOverviewData.get('hasFetchDealRollupsError'));
export const getHasAggregatedOverviewDataGoalsFetchError = createSelector([getAggregatedOverviewData], aggregatedOverviewData => aggregatedOverviewData.get('hasFetchGoalsError'));
export const getHasAggregatedOverviewDataSubmissionsFetchError = createSelector([getAggregatedOverviewData], aggregatedOverviewData => aggregatedOverviewData.get('hasFetchSubmissionsError'));
export const getHasAggregatedOverviewFetchError = createSelector([getHasAggregatedOverviewDataDealRollupsFetchError, getHasAggregatedOverviewDataGoalsFetchError, getHasAggregatedOverviewDataSubmissionsFetchError, getIsUngatedToForecastingMultipleOutcomes, getHasForecastTypesFetchError], (hasAggregatedOverviewDataDealRollupsFetchError, hasAggregatedOverviewDataGoalsFetchError, hasAggregatedOverviewDataSubmissionsFetchError, isUngatedToForecastingMultipleOutcomes, hasForecastTypesFetchError) => hasAggregatedOverviewDataDealRollupsFetchError || hasAggregatedOverviewDataGoalsFetchError || hasAggregatedOverviewDataSubmissionsFetchError || isUngatedToForecastingMultipleOutcomes && hasForecastTypesFetchError);